import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Typography,
  Button,
  GlobalStyles,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { games } from "./FeaturedGames";
import axios from "axios";
import { API_BASE_URL } from "../apiConfig";
import AppDialog from "./AppDialog";
import { useLoading } from "../context/LoadingContext";

export default function FeaturedGames() {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const scrollRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);

  const [snaps, setSnaps] = useState([]);
  const [, setSelectedIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [dialog, setDialog] = useState({ open: false, type: "error", message: "" });
  const showDialog = (type, message) => setDialog({ open: true, type, message });
  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));

  const isDesktop = useMediaQuery("(min-width:900px)");
  // number of visible items per slide
  const visibleCount = isDesktop ? 3 : 1;
  const dotsToShow = Math.max(games.length - visibleCount + 1, 1);

  const [stations, setStations] = useState([]);

  useEffect(() => {
    const fetchStations = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/stations`);
        if (res.data.status === "success") {
          setStations(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch stations", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStations();
  }, [setLoading]);

  const commonThumbnailMap = useMemo(() => {
    const map = {};
    stations.forEach((station) => {
      if (station.type && station.common_thumbnail_url) {
        map[station.type] = station.common_thumbnail_url;
      }
    });
    return map;
  }, [stations]);

  const thumbnailMap = useMemo(() => {
    const map = {};
    stations.forEach((station) => {
      if (station.type && station.thumbnail_url && !map[station.type]) {
        map[station.type] = station.thumbnail_url;
      }
    });
    return map;
  }, [stations]);

  const descriptionMap = useMemo(() => {
    const map = {};
    stations.forEach((station) => {
      if (station.type && station.description && !map[station.type]) {
        map[station.type] = station.description;
      }
    });
    return map;
  }, [stations]);

  const handleBookingClick = (game) => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      showDialog("error", "Please login to continue booking!");
      navigate("/sign-in");
      return;
    }

    navigate("/booking", {
      state: {
        from: "/games",
        stationType:
          game.title === "PS5 Stations"
            ? "PlayStation"
            : game.title === "Pool Tables"
              ? "Pool"
              : "Simulator",
      },
    });
  };

  // calculate left offsets for each slide
  const calcSnaps = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const children = Array.from(container.querySelectorAll(".slide-item"));
    const positions = children.map((child) => child.offsetLeft);
    setSnaps(positions);
  }, []);

  useEffect(() => {
    calcSnaps();
    const container = scrollRef.current;
    if (!container) return;

    const imgs = Array.from(container.querySelectorAll("img"));
    const onImgLoad = () => calcSnaps();
    imgs.forEach((img) => img.addEventListener("load", onImgLoad));
    window.addEventListener("resize", calcSnaps);
    const t = setTimeout(calcSnaps, 120);

    return () => {
      imgs.forEach((img) => img.removeEventListener("load", onImgLoad));
      window.removeEventListener("resize", calcSnaps);
      clearTimeout(t);
    };
  }, [calcSnaps]);

  // scroll to a particular snap
  const scrollToSnap = useCallback(
    (snapIndex) => {
      const container = scrollRef.current;
      if (!container || snaps.length === 0) return;
      const left = snaps[snapIndex] || 0;
      container.scrollTo({ left, behavior: "smooth" });
    },
    [snaps],
  );

  // Scroll to the starting slide of that dot
  const handleDotClick = (dotIdx) => {
    scrollToSnap(dotIdx);
  };

  // Update active dot while scrolling
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || snaps.length === 0) return;

    const onScroll = () => {
      const scrollLeft = container.scrollLeft;
      // Find the first fully visible item
      let firstVisibleIndex = 0;
      for (let i = 0; i < snaps.length; i++) {
        if (scrollLeft <= snaps[i]) {
          firstVisibleIndex = i;
          break;
        }
      }
      // Clamp the active dot to max possible dot
      setSelectedIndex(Math.min(firstVisibleIndex, dotsToShow - 1));
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => container.removeEventListener("scroll", onScroll);
  }, [snaps, dotsToShow]);

  // drag-to-scroll
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let isDown = false;
    let startX = 0;
    let scrollStart = 0;

    const onPointerDown = (e) => {
      // Don't interfere with buttons or interactive elements
      if (e.target.closest("button") || e.target.closest("a")) {
        return;
      }
      isDown = true;
      container.style.cursor = "grabbing";
      startX = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
      scrollStart = container.scrollLeft;
      if (e.pointerId) container.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
      if (!isDown) return;
      const x = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
      const diff = Math.abs(startX - x);
      if (diff > 5) {
        container.scrollLeft = scrollStart + (startX - x);
      }
    };
    const onPointerUp = (e) => {
      isDown = false;
      container.style.cursor = "grab";
      if (e?.pointerId) container.releasePointerCapture?.(e.pointerId);
    };

    container.addEventListener("pointerdown", onPointerDown);
    container.addEventListener("pointermove", onPointerMove);
    container.addEventListener("pointerup", onPointerUp);
    container.addEventListener("pointerleave", onPointerUp);
    container.style.cursor = "grab";

    return () => {
      container.removeEventListener("pointerdown", onPointerDown);
      container.removeEventListener("pointermove", onPointerMove);
      container.removeEventListener("pointerup", onPointerUp);
      container.removeEventListener("pointerleave", onPointerUp);
      container.style.cursor = "";
    };
  }, []);

  // Auto-play functionality with Intersection Observer
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsAutoPlaying(true);
          } else {
            setIsAutoPlaying(false);
          }
        });
      },
      { threshold: 0.3 },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Auto-scroll effect
  useEffect(() => {
    if (!isAutoPlaying || snaps.length === 0) {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
        autoPlayIntervalRef.current = null;
      }
      return;
    }

    autoPlayIntervalRef.current = setInterval(() => {
      setSelectedIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % dotsToShow;
        scrollToSnap(nextIndex);
        return nextIndex;
      });
    }, 3000); // Change slide every 3 seconds

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [isAutoPlaying, snaps, dotsToShow, scrollToSnap]);

  return (
    <>
      <GlobalStyles
        styles={{
          "@font-face": {
            fontFamily: "BRUSHSTRIKE",
            src: `url("/fonts/Brushstrike.ttf") format("truetype")`,
          },
        }}
      />

      <Box
        component="section"
        id="popular-games"
        sx={{
          position: "relative",
          bgcolor: "#0A0D17",
          py: { xs: 4, md: 2 },
          px: { xs: 2, sm: 4 },
          color: "white",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: "60%",
            left: "50%",
            width: "800px",
            height: "800px",
            background:
              "radial-gradient(circle, rgba(51, 178, 247, 0.73), rgba(84, 14, 92, 0.6), transparent 50%)",
            transform: "translate(-50%, -50%)",
            filter: "blur(120px)",
            zIndex: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            maxWidth: "1200px",
            mx: "auto",
            textAlign: "center",
            mb: { xs: 4, md: 6 },
          }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: { xs: "32px", sm: "44px", md: "75px" },
              fontFamily: "BRUSHSTRIKE",
              fontWeight: 400,
              background:
                "linear-gradient(to right, #A033FF, #D100FF, #00C3FF)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: { xs: 1.5, md: 2 },
              lineHeight: 1.3,
            }}
          >
            Popular Games
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "gray.400",
              maxWidth: 950,
              mx: "auto",
              fontSize: { xs: "11px", sm: "14px", md: "16px" },
              lineHeight: { xs: 1.4, md: 1.6 },
              px: { xs: 1, sm: 0 },
            }}
          >
            Dive into our hottest picks! From immersive VR worlds and thrilling
            racing simulators to classic arcade battles and next-gen PS5 action
            — these are the games that define the ultimate gaming experience.
          </Typography>
        </Box>

        {/* Carousel */}
        <Box
          sx={{
            width: "100%",
            overflowX: "auto",
            pb: 2,
            "&::-webkit-scrollbar": { display: "none" },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            maxWidth: "1200px",
            mx: "auto",
          }}
          ref={scrollRef}
        >
          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, sm: 3 },
              px: { xs: 1, sm: 0 },
            }}
          >
            {games.map((game, index) => {
              const image = commonThumbnailMap[game.category] || thumbnailMap[game.category] || game.img;
              const description = descriptionMap[game.category] || game.description;

              return (
                <Box
                  key={index}
                  className="slide-item"
                  onClick={() => handleDotClick(index)}
                  sx={{
                    flex: "0 0 auto",
                    width: { xs: 200, sm: 280, md: 380 },
                    height: { xs: 280, sm: 380, md: 500 },
                    position: "relative",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    paddingBottom: "4px",
                    "&:hover": {
                      transform: { xs: "none", md: "scale(1.03)" },
                    },
                    "&:hover .game-title": { color: "#33B2F7 !important" },
                    "&:hover .bottom-glow": {
                      opacity: 1,
                    },
                    "&:hover .mobile-gradient-border": {
                      opacity: { xs: 1, sm: 0, md: 0 },
                    },
                  }}
                >
                  {/* Mobile gradient border on hover */}
                  <Box
                    className="mobile-gradient-border"
                    sx={{
                      display: { xs: "block", sm: "none" },
                      position: "absolute",
                      inset: 0,
                      borderRadius: "4px",
                      padding: "2px",
                      background:
                        "linear-gradient(to right, #A033FF, #D100FF, #00C3FF)",
                      WebkitMask:
                        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                      pointerEvents: "none",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      zIndex: 5,
                    }}
                  />
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      clipPath: [
                        "polygon(10% 0, 100% 0, 80% 100%, 0 90%)",
                        "polygon(15% 0, 98% 0, 100% 100%, 0 100%)",
                        "polygon(2% 0, 98% 0, 90% 100%, 0 100%)",
                        "polygon(20% 0, 95% 0, 98% 100%, 0% 100%)",
                        "polygon(0% 0, 100% 0, 80% 100%, 0 100%)",
                      ][index % 5],
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        padding: "2px",
                        background:
                          "linear-gradient(to right, #9F00FF, #B86BFF, #00D3FE, #3C7CFA)",
                        WebkitMask:
                          "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                        pointerEvents: "none",
                        zIndex: 1,
                      },
                    }}
                  >
                    {/* Bottom white line on hover */}
                    <Box
                      className="bottom-glow"
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "#FFFFFF",
                        opacity: 0,
                        transition: "opacity 0.3s ease",
                        zIndex: 10,
                      }}
                    />
                    <Box
                      component="img"
                      src={image}
                      alt={game.title}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = game.img;
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />

                    {/* Overlay */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: { xs: "32%", sm: "34%", md: "36%" },
                        left: 0,
                        right: 0,
                        zIndex: 10,
                        pointerEvents: "auto",
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      onPointerMove={(e) => e.stopPropagation()}
                    >
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleBookingClick(game);
                        }}
                        sx={{
                          width: "100%",
                          py: { xs: 0.8, sm: 1, md: 1.3 },
                          borderRadius: 0,
                          fontWeight: "bold",
                          fontSize: { xs: "11px", sm: "15px", md: "19px" },
                          textTransform: "none",
                          color: "#fff",
                          background:
                            "linear-gradient(to right, #A905BC, #33B2F7)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            background:
                              "linear-gradient(to right, #33B2F7, #A905BC)",
                          },
                        }}
                      >
                        Booking Now
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 2,
                        right: 0,
                        height: { xs: "40%", sm: "42%", md: "44%" },
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          bgcolor: "rgba(0,0,0)",
                          p: { xs: 1, sm: 1.5, md: 2.1 },
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                          gap: { xs: 0.25, md: 0.5 },
                        }}
                      >
                        <Typography
                          className="game-title"
                          variant="h6"
                          sx={{
                            mb: 0.1,
                            fontSize: { xs: "12px", sm: "18px", md: "24px" },
                            fontWeight: 700,
                            transition: "color 0.3s ease",
                            color: "#FFFFFF",
                          }}
                        >
                          {game.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: { xs: "8px", sm: "13px", md: "16px" },
                            color: "#FFFFFF",
                            lineHeight: { xs: 1.2, md: 1.32 },
                          }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 4, md: 6 },
          }}
        ></Box>
      </Box>
      <AppDialog open={dialog.open} onClose={closeDialog} type={dialog.type} message={dialog.message} />
    </>
  );
}
